import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

import type { OrganizationType, OrganizationUsersType } from "@interfaces/organization.type";

interface IOrganizationStore {
  actions: {
    clearOrganization: () => void;
    getOrganization: () => OrganizationType | undefined;
    getOrganizationUsers: () => OrganizationUsersType | undefined;
    getSelectedAssignees: () => string[];
    setOrganization: (organizationData: OrganizationType) => void;
    setOrganizationUsers: (organizationUsersData: OrganizationUsersType) => void;
    setSelectedAssignees: (assignees: string[]) => void;
  };
  organization?: OrganizationType;
  organizationUsers?: OrganizationUsersType;
  selectedAssignees: string[];
}

/**
 * Create the Zustand store for managing the organization state.
 */
const OrganizationStore = createStore<IOrganizationStore>()((set, get) => ({
  actions: {
    clearOrganization: () => set(() => ({ organization: undefined, organizationUsers: undefined })),
    getOrganization: () => get().organization,
    getOrganizationUsers: () => get().organizationUsers,
    getSelectedAssignees: () => get().selectedAssignees,
    setOrganization: (organizationData) => set(() => ({ organization: organizationData })),
    setOrganizationUsers: (organizationUsersData) => set(() => ({ organizationUsers: organizationUsersData })),
    setSelectedAssignees: (assignees) => {
      localStorage.setItem("selectedAssignees", JSON.stringify(assignees));
      set(() => ({ selectedAssignees: assignees }));
    },
  },
  organization: undefined,
  organizationUsers: undefined,
  selectedAssignees: JSON.parse(localStorage.getItem("selectedAssignees") || "[]") as string[],
}));

/**
 * A custom hook to access the organization state.
 */
function useOrganizationStore(): IOrganizationStore;
function useOrganizationStore<Type>(selector: (state: IOrganizationStore) => Type): Type;
function useOrganizationStore<Type>(selector?: (state: IOrganizationStore) => Type) {
  return useStore(OrganizationStore, selector!);
}

export { OrganizationStore, useOrganizationStore };
